import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { AuthService } from '../../../auth/auth.service';
import type { IconDef } from '../icons/icon';
import { IconComponent } from '../icons/icon-reusable';
import { backsectionIcon, submitEvaluationIcon, whiteEditIcon } from '../icons/icon-second';
import { ButtonComponent } from '../button-reusable/button-reusable';

export type EvaluationOutcome = 'pass' | 'fail';

export interface EvaluationSubmitRequest {
	outcome: EvaluationOutcome | null;
	comments: string;
}

export interface EvaluationSubmitEvent extends EvaluationSubmitRequest {
	failureReason: string;
}

@Component({
	selector: 'app-user-application-form-layout',
	standalone: true,
	imports: [CommonModule, IconComponent, ButtonComponent],
	templateUrl: './user-application-form-layout.html',
})
export class UserApplicationFormLayout implements OnChanges {
	private readonly authService = inject(AuthService);
	private readonly defaultHeadingIcon = whiteEditIcon;
	private readonly defaultActionIcon = backsectionIcon;
	protected readonly submitEvaluationIcon = submitEvaluationIcon;

	@Input() heading = 'Heading';
	@Input() subheading = 'Sub Heading';
	@Input() headingIcon: IconDef | null = null;
	@Input() actionLabel = '';
	@Input() actionIcon: IconDef | null = null;

	@Input() evaluationTitle = 'Evaluation';
	@Input() evaluationSubtitle = 'Interview Outcome';
	@Input() passLabel = 'Pass';
	@Input() passDescription = 'Initiates the application and email credentials.';
	@Input() passStatusText = 'Status will move to initiated.';
	@Input() failLabel = 'Fail';
	@Input() failDescription =
		'Halts user creation. No credentials are sent and the record is saved for audit.';
	@Input() failStatusText = 'Status will be interview failed.';
	@Input() commentsLabel = 'Additional Comments';
	@Input() commentsHelper =
		'Provide any additional comments to support your assessment and recommendation.';
	@Input() commentsPlaceholder = 'Enter your additional comments here...';
	@Input() comments = '';
	@Input() maxCommentLength = 500;
	@Input() submitLabel = 'Submit Evaluation';
	@Input() disableSubmit = false;
	@Input() outcome: EvaluationOutcome | null = null;
	@Input() evaluationConfirmationOpen = false;
	@Input() reviewedBy = '';
	@Input() interviewDate: string | Date | null = null;

	@Output() actionClick = new EventEmitter<void>();
	@Output() outcomeChange = new EventEmitter<EvaluationOutcome | null>();
	@Output() commentsChange = new EventEmitter<string>();
	@Output() evaluationConfirmationOpenChange = new EventEmitter<boolean>();
	@Output() submitRequested = new EventEmitter<EvaluationSubmitRequest>();
	@Output() submit = new EventEmitter<EvaluationSubmitEvent>();

	readonly outcomeGroupName = `evaluation-outcome-${Math.random().toString(36).slice(2, 9)}`;
	protected showOutcomeError = false;
	protected failureReason = '';
	protected showFailureReasonError = false;
	private confirmationOpenedAt: Date | null = null;

	get commentCount(): number {
		return this.comments?.length ?? 0;
	}

	get resolvedHeadingIcon(): IconDef | null {
		return this.headingIcon ?? this.defaultHeadingIcon;
	}

	get resolvedActionIcon(): IconDef | null {
		return this.actionIcon ?? this.defaultActionIcon;
	}

	get confirmationTitle(): string {
		return this.outcome === 'fail' ? 'Candidate Did Not Pass' : 'Candidate Passed Interview';
	}

	get confirmationDescription(): string {
		return this.outcome === 'fail'
			? 'The candidate did not meet the interview requirements. Their profile will remain inactive and no user account will be created at this stage.'
			: 'The candidate has successfully cleared the interview round. You can now proceed with creating their user account and onboarding them into the system.';
	}

	get confirmationStatusLabel(): string {
		return this.outcome === 'fail' ? 'Failed' : 'Passed';
	}

	get reviewerDisplayName(): string {
		if (this.reviewedBy.trim()) {
			return this.reviewedBy.trim();
		}

		const session = this.authService.session();
		const fullName = `${session.firstName ?? ''} ${session.lastName ?? ''}`.trim();
		return fullName || session.email?.trim() || session.company?.trim() || 'Current User';
	}

	get formattedInterviewDate(): string {
		const date = this.resolveInputDate(this.interviewDate) ?? this.confirmationOpenedAt ?? new Date();
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		const hours24 = date.getHours();
		const hours12 = hours24 % 12 || 12;
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const meridiem = hours24 >= 12 ? 'PM' : 'AM';

		return `${day}-${month}-${year}, ${String(hours12).padStart(2, '0')}.${minutes} ${meridiem}`;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['evaluationConfirmationOpen']) {
			const isOpen = Boolean(changes['evaluationConfirmationOpen'].currentValue);
			const wasOpen = Boolean(changes['evaluationConfirmationOpen'].previousValue);

			if (isOpen && !wasOpen) {
				this.confirmationOpenedAt = this.resolveInputDate(this.interviewDate) ?? new Date();
				this.showFailureReasonError = false;

				if (this.outcome !== 'fail') {
					this.failureReason = '';
				}
			}

			if (!isOpen && wasOpen) {
				this.resetConfirmationState();
			}
		}

		if (changes['outcome'] && this.outcome !== 'fail') {
			this.failureReason = '';
			this.showFailureReasonError = false;
		}
	}

	onActionClick(): void {
		this.actionClick.emit();
	}

	selectOutcome(outcome: EvaluationOutcome): void {
		if (this.outcome === outcome) {
			return;
		}

		this.outcome = outcome;
		this.showOutcomeError = false;
		if (outcome !== 'fail') {
			this.failureReason = '';
			this.showFailureReasonError = false;
		}
		this.outcomeChange.emit(this.outcome);
	}

	onCommentsInput(event: Event): void {
		const target = event.target as HTMLTextAreaElement | null;
		const value = target?.value ?? '';

		this.comments = value;
		this.commentsChange.emit(value);
	}

	onSubmitRequest(): void {
		if (!this.outcome) {
			this.showOutcomeError = true;
			return;
		}

		this.showOutcomeError = false;
		this.submitRequested.emit({ outcome: this.outcome, comments: this.comments });
	}

	onConfirmationBackdropClick(event: MouseEvent): void {
		if (event.target !== event.currentTarget) {
			return;
		}

		this.closeConfirmation();
	}

	onFailureReasonInput(event: Event): void {
		const target = event.target as HTMLTextAreaElement | null;
		this.failureReason = target?.value ?? '';
		if (this.failureReason.trim()) {
			this.showFailureReasonError = false;
		}
	}

	onConfirmSubmit(): void {
		if (!this.outcome) {
			this.showOutcomeError = true;
			return;
		}

		const failureReason = this.outcome === 'fail' ? this.failureReason.trim() : '';
		if (this.outcome === 'fail' && !failureReason) {
			this.showFailureReasonError = true;
			return;
		}

		this.submit.emit({
			outcome: this.outcome,
			comments: this.comments,
			failureReason,
		});
	}

	closeConfirmation(): void {
		if (this.disableSubmit) {
			return;
		}

		this.evaluationConfirmationOpen = false;
		this.evaluationConfirmationOpenChange.emit(false);
		this.resetConfirmationState();
	}

	private resetConfirmationState(): void {
		this.showFailureReasonError = false;
		this.failureReason = '';
		this.confirmationOpenedAt = null;
	}

	private resolveInputDate(value: string | Date | null): Date | null {
		if (!value) {
			return null;
		}

		if (value instanceof Date) {
			return Number.isNaN(value.getTime()) ? null : value;
		}

		const parsedDate = new Date(value);
		return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
	}
}
